import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useI18n } from "i18n";

interface ExcelPreviewProps {
  open: boolean;
  onClose: () => void;
  excelBlob: Blob | null;
  fileName: string;
}

export const ExcelPreview: React.FC<ExcelPreviewProps> = ({
  open,
  onClose,
  excelBlob,
  fileName,
}) => {
  const { t } = useI18n();

  const handleDownload = () => {
    if (excelBlob) {
      const url = URL.createObjectURL(excelBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: "400px" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">{t("report.excelReadyTitle")}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "250px",
            textAlign: "center",
            p: 3,
          }}
        >
          {/* ファイルアイコン */}
          <Box
            sx={{
              fontSize: "4rem",
              mb: 2,
              color: "success.main",
            }}
          >
            📊
          </Box>

          {/* メッセージ */}
          <Typography variant="h6" gutterBottom color="text.primary">
            {t("report.excelGeneratedTitle")}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t("report.excelGeneratedMessage")}
          </Typography>

          {/* ファイル情報 */}
          <Box
            sx={{
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "grey.200",
              minWidth: "300px",
            }}
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              <Box component="span" sx={{ fontWeight: "bold", color: "text.primary" }}>
                {t("report.fileName")}
              </Box>{" "}
              <Box component="span" sx={{ color: "text.secondary" }}>
                {fileName}
              </Box>
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 1 }}>
              <Box component="span" sx={{ fontWeight: "bold", color: "text.primary" }}>
                {t("report.fileSize")}
              </Box>{" "}
              <Box component="span" sx={{ color: "text.secondary" }}>
                {Math.round((excelBlob?.size || 0) / 1024)} KB
              </Box>
            </Typography>
            
            <Typography variant="body2">
              <Box component="span" sx={{ fontWeight: "bold", color: "text.primary" }}>
                {t("report.fileType")}
              </Box>{" "}
              <Box component="span" sx={{ color: "text.secondary" }}>
                {t("report.excelWorkbook")}
              </Box>
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} variant="outlined" sx={{ mr: 1 }}>
          {t("common.close")}
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={!excelBlob}
        >
          {t("report.downloadExcel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
